import { HttpErrorResponse } from '@angular/common/http';
import { AlertService } from '../services/alert.service';
import { ErrorReportingService } from '../services/error-reporting.service';

interface ErrorMessages {
  cause: string;
  /**
   * Consejo ESPECÍFICO y accionable para este error concreto.
   * Es opcional: NO debe incluir la recomendación genérica de
   * "reintentá / contactá a soporte", porque el AlertService ya agrega
   * automáticamente ese texto (DEFAULT_SUPPORT_MESSAGE) al final de cada
   * error/warning/info. Si el único consejo posible es el genérico,
   * dejá `solution` sin definir: se mostrará cause + support, sin repetición.
   */
  solution?: string;
  silent?: true;
}

type EndpointErrorMap = Record<string, Partial<Record<number, ErrorMessages>>>;

// ─── Mensajes genéricos ────────────────────────────────────────────────────────

const GENERIC_ERROR: ErrorMessages = {
  cause: 'Ocurrió un error inesperado.',
  // Sin solution: el support ya cubre "reintentá / contactá a soporte".
};

const HTTP_STATUS_MESSAGES: Partial<Record<number, ErrorMessages>> = {
  0: {
    cause:    'No se pudo conectar con el servidor.',
    solution: 'Verificá tu conexión a internet.',
  },
  400: {
    cause:    'Los datos enviados no son válidos.',
    solution: 'Revisá los campos del formulario.',
  },
  401: {
    cause:    'No tenés autorización para realizar esta acción.',
    solution: 'Verificá que tu sesión esté activa.',
  },
  403: {
    cause:    'No tenés permisos suficientes para esta operación.',
    solution: 'Contactá a tu administrador para solicitar acceso.',
  },
  404: {
    cause: 'No se pudo obtener la información solicitada.',
  },
  409: {
    cause:    'Existe un conflicto con los datos ingresados.',
    solution: 'Es posible que el registro ya exista. Revisá la información.',
  },
  500: {
    cause: 'Ocurrio un evento inesperado.',
  },
};

// ─── Mensajes por endpoint ─────────────────────────────────────────────────────

const ENDPOINT_MESSAGES: EndpointErrorMap = {
  /* Empresa Controler */
  '/wsActivarPV': {
    401: {
      cause:    'No tenés permisos de acceso ROOT para realizar esta acción.',
      solution: 'Contactá a tu administrador para solicitar los permisos necesarios.',
    }
  },
  '/wsModificarLogoEmpresa': {
    400: {
      cause: 'Datos invalidos.',
    }
  },
  '/wsActualizarEmpresa': {
    401: {
      cause:    'No tenés autorización para actualizar la empresa.',
      solution: 'Verificá que tengas permisos para operar con esta empresa.',
    }
  },
  '/wsRootCsObtenerListadoEmpresas': {
    400: { silent: true, cause: 'Camino no contemplado', solution: 'Contactá a tu administrador para notificar este error' },
    401: {
      cause:    'No tenés autorización para ver el listado de las empresas.',
      solution: 'Verificá que tengas permisos para operar.',
    }
  },
  '/wsObtenerDatosEmpresa': {
    401: {
      cause:    'No tenés autorización para ver los datos de la empresa.',
      solution: 'Verificá que tengas permisos para operar con esta empresa.',
    }
  },
  /* Usuario Controller */
  '/wsModificarDatosUsuarioLogueado': {
    400: {
      cause:    'No se pudieron guardar los cambios.',
      solution: 'Revisá los datos ingresados.',
    },
    401: {
      cause:    'No tenés autorización para modificar los datos del usuario.',
      solution: 'Verificá que tengas permisos para operar.',
    },
  },
  '/wsModificarClaveUsuarioLogueado': {
    400: {
      cause:    'La clave actual y la nueva son requeridas.',
      solution: 'Completá ambos campos.',
    },
    401: {
      cause:    'La clave actual ingresada no es correcta.',
      solution: 'Verificá tu clave actual.',
    },
  },
  '/wsActualizarDatosCuentaUsuario': {
    401: {
      cause:    'No tenés autorización para modificar los datos del usuario.',
      solution: 'Verificá que tengas permisos para operar.',
    },
  },
  '/wsSolicitarReseteoClaveUsuarioGeneral': {
    400: {
      cause:    'No se pudo realizar la petición.',
      solution: 'Verificá que el link del email esté bien escrito.',
    },
    404: {
      cause:    'El email no está asociado a ninguna cuenta en la plataforma.',
      solution: 'Verificá que el link del email esté bien escrito.',
    },
  },
  '/wsReseteoClaveUsuarioGeneral': {
    404: {
      cause:    'El tiempo para cambiar tu clave ya expiró.',
      solution: 'Volvé a solicitar el cambio de clave desde la plataforma para recibir un nuevo email.',
    },
  },
  '/wsResetearClaveCuentaUsuario': {
    400: {
      cause:    'Hubo un problema al cambiar la clave.',
      solution: 'Verificá que el usuario tenga un email válido configurado.',
    },
    401: {
      cause:    'No tenés autorización para modificar los datos del usuario.',
      solution: 'Verificá que tengas permisos para operar.',
    },
  },
  '/wsObtenerListadoUsuarioPorEmpresa': {
    401: {
      cause:    'No tenés autorización para ver el listado de los usuarios.',
      solution: 'Verificá que tengas permisos para operar.',
    },
  },
  '/wsObtenerDatosUsuarioPorEmpresa': {
    400: {
      cause:    'No se pudieron obtener los datos del usuario.',
      solution: 'Verificá que el usuario exista y que tengas permisos para accederlo.',
    },
    401: {
      cause:    'No tenés autorización para ver los datos del usuario.',
      solution: 'Verificá que tengas permisos para operar.',
    },
  },
  '/wsGenerarUsuario': {
    400: {
      cause:    'Ocurrió un error al crear el usuario.',
      solution: 'Verificá que el email no esté ya registrado en el sistema y que todos los datos sean correctos.',
    },
    401: {
      cause:    'No tenés autorización para crear el usuario.',
      solution: 'Verificá que tengas permisos para operar.',
    },
  },
  '/wsEliminarUsuario': {
    400: {
      cause:    'Ocurrió un error al eliminar el usuario.',
      solution: 'Verificá que el usuario no tenga operaciones activas o datos asociados que impidan su eliminación.',
    },
    401: {
      cause:    'No tenés autorización para eliminar el usuario.',
      solution: 'Verificá que tengas permisos para operar.',
    },
  },
  // no se añade porque es un error personalizado de global service y tiene otro trato
  // '/wsObtenerDatosUsuarioLogueado': {404: { silent: true, cause: '', solution: '' },},
  /* Rol Controller */
  '/wsActualizarRole': {
    400: {
      cause:    'Ocurrió un error al actualizar el rol.',
      solution: 'Verificá que hayas seleccionado al menos un permiso y que el nombre del rol sea único.',
    },
    401: {
      cause:    'No tenés autorización para modificar los datos del rol.',
      solution: 'Verificá que tengas permisos para operar.',
    },
  },
  '/wsObtenerListadoRolePorEmpresa': {
    400: {
      cause:    'No se pudieron obtener los datos del rol.',
      solution: 'Verificá que el rol exista y que tengas permisos para accederlo.',
    },
    401: {
      cause:    'No tenés autorización para ver el listado de los roles.',
      solution: 'Verificá que tengas permisos para operar.',
    },
  },
  '/wsObtenerDatosRolePorEmpresa': {
    400: {
      cause:    'No se pudieron obtener los datos del rol.',
      solution: 'Verificá que el rol exista y que tengas permisos para accederlo.',
    },
    401: {
      cause:    'No tenés autorización para ver los datos del rol.',
      solution: 'Verificá que tengas permisos para operar.',
    },
  },
  '/wsGenerarRole': {
    400: {
      cause:    'Ocurrió un error al crear el rol.',
      solution: 'Verificá que hayas seleccionado al menos un permiso y que el nombre del rol sea único.',
    },
    401: {
      cause:    'No tenés autorización para crear el rol.',
      solution: 'Verificá que tengas permisos para operar.',
    },
  },
  '/wsEliminarRole': {
    400: {
      cause:    'Ocurrió un error al eliminar el rol.',
      solution: 'Este rol está asignado a usuarios activos, por lo que no puede eliminarse.',
    },
    401: {
      cause:    'No tenés autorización para eliminar el rol.',
      solution: 'Verificá que tengas permisos para operar.',
    },
  },
  /* Cliente Controller */
  '/wsActualizarCliente': {
    400: {
      cause:    'No se pudieron guardar los cambios.',
      solution: 'Revisá los datos ingresados.',
    },
    401: {
      cause:    'No tenés autorización para modificar los datos del cliente.',
      solution: 'Verificá que tengas permisos para operar.',
    },
  },
  '/wsObtenerListadoClientePorEmpresa': {
    401: {
      cause:    'No tenés autorización para ver el listado de los clientes.',
      solution: 'Verificá que tengas permisos para operar.',
    },
    404: {
      cause:    'No se encontraron resultados para tu búsqueda.',
      solution: 'Probá con otros términos de búsqueda.',
    },
  },
  '/wsObtenerDatosClientePorEmpresa': {
    400: {
      cause:    'No se pudieron obtener los datos del cliente.',
      solution: 'Verificá que el cliente exista y que tengas permisos para accederlo.',
    },
    401: {
      cause:    'No tenés autorización para ver los datos del cliente.',
      solution: 'Verificá que tengas permisos para operar.',
    },
  },
  '/wsGenerarCliente': {
    400: {
      cause:    'Ocurrió un error al crear el cliente.',
      solution: 'Verificá que los datos del cliente estén bien o si su plan actual se encuentra desactualizado.',
    },
    401: {
      cause:    'No tenés autorización para crear el cliente.',
      solution: 'Verificá que tengas permisos para operar.',
    },
    409: {
      cause:    'Ya existe un cliente registrado con ese número de documento.',
      solution: 'Revisá el listado de clientes o ingresá un documento diferente para continuar.'
    }
  },
  '/wsEliminarCliente': {
    400: {
      cause:    'Ocurrió un error al eliminar el cliente.',
      solution: 'Este cliente ya cuenta con cobros creados, por lo que no es posible eliminarlo.',
    },
    401: {
      cause:    'No tenés autorización para eliminar el cliente.',
      solution: 'Verificá que tengas permisos para operar.',
    },
  },
  /* Cuenta Controller */
  /*
  El Guard validacion-plan.guards se encarga la logica de validación
  '/wsValidarAccesoAdicionales': {},*/
  '/wsGenerarCuentaSimplificada': {
    400: {
      cause:    'El email está asociado a una cuenta en la plataforma.',
      solution: 'Verificá que el email esté bien escrito.',
    },
  },
  '/wsEnviarDatosFiscales': {
    401: {
      cause:    'No tenés autorización para acceder a esta Empresa.',
      solution: 'Verificá que tengas permisos para operar.',
    },
  },
  /* NOtificaciones-in-app-controller Controller */
  '/wsObtenerNotificacionesParaCampana': {
    401: {
      cause:    'No tenés autorización.',
      solution: 'Verificá que tengas permisos para operar.',
    },
    404: { silent: true, cause: '', solution: '' },
  },
  '/wsObtenerNotificacionesInApp': {
    401: {
      cause:    'No tenés autorización.',
      solution: 'Verificá que tengas permisos para operar.',
    },
    404: {
      cause:    'No se encontraron resultados para tu búsqueda.',
      solution: 'Probá con otros términos de búsqueda.',
    },
  },
  '/wsObtenerDatosNotificacionInApp': {
    401: {
      cause:    'No tenés autorización.',
      solution: 'Verificá que tengas permisos para operar.',
    },
  },
  //'/wsGenerarNotificacionInApp': {},
  '/wsEnviarNotificacionInAppViaPush': {
    400: {
      cause:    'La notificación no existe.',
      solution: 'Verifica en el listado que exista la notificación.',
    },
    404: {
      cause:    'La notificación ya fue enviada.',
      solution: 'Verifica en el listado el estado no se encuentre en enviada "Si"',
    },
  },
  '/wsEliminarNotificacionInApp': {
    400: {
      cause:    'La notificación no existe.',
      solution: 'Verifica en el listado que exista la notificación.',
    }
  },
  /* Provincia Controller */
  '/wsObtenerListadoProvinciaPorPais': {
    400: {
      cause: 'No se pudieron obtener las provincias.',
    },
    404: {
      cause:    'No se encontraron provincias para el país seleccionado.',
      solution: 'Verificá el país seleccionado.',
    },
  },
  /* Permission Controller */
  '/wsObtenerListadoPermission': {
    401: {
      cause:    'No tenés autorización.',
      solution: 'Verificá que tengas permisos para operar.',
    },
    404: {
      cause: 'No se encontraron permisos.',
    },
  },
  /* Comprobante Controller */
  /* Cobro Controller */
  /* Movimiento Caja Controller */
  /* Caja Cierre Controller */
  /* Actividad Controller */
  '/wsObtenerActividades': {
    401: {
      cause:    'CUIT invalido, No tenés autorización.',
      solution: 'Verificá que el cuit esté correctamente.',
    },
    404: {
      cause: 'No se encontraron actividades asociados al cuit ingresado.',
    },
  },
  /* Login Controller */
  '/rest/login': {
    401: {
      cause:    'El email o la clave que escribiste no son correctos.',
      solution: 'Verificá los datos ingresados o usá la opción "¿Olvidaste tu clave?" para generar una nueva clave.',
    },
    404: {
      cause:    'El email o la clave que escribiste no son correctos.',
      solution: 'Verificá los datos ingresados o usá la opción "¿Olvidaste tu clave?" para generar una nueva clave.',
    },
    500: {
      cause:    'Las credenciales ingresadas no son válidas.',
      solution: 'Verificá tu usuario y clave.',
    },
  },
  '/rest/refresh-token': {
    // 401 silenciado: el ErrorHandlerInterceptor detecta este caso y hace logout directo.
    // No se muestra alert porque el logout ya redirige al login automáticamente.
    401: { silent: true, cause: '', solution: '' },

    // 400: el request llegó mal formado al servidor pero el refresh token puede seguir válido.
    // Se informa al usuario para que reintente el cambio de empresa.
    400: {
      cause:    'Tu sesión ha expirado.',
      solution: 'Por favor, iniciá sesión nuevamente.',
    },
  },
  /* Parametros Generales Controller */
  '/wsObtenerParametrosGenerales': {},
  /* Unidades Controller */
  '/wsObtenerListadoUnidad': {},
  /* Tipo Responsabilidad IVA Controller */
  '/wsObtenerListadoTipoResponsableIva': {},
  /* Tipo Responsabilidad IVA Controller */
  '/wsObtenerListadoTipoMoneda': {},
  /* Tipo Responsabilidad IVA Controller */
  '/wsObtenerListadoTipoDocumento': {},
  /* Tipo Comprobante Controller */
  '/wsObtenerListadoTipoComprobante': {},
  /* Pais Controller */
  '/wsObtenerListadoPais': {
    400: {
      cause: 'No se pudieron obtener los paises.',
    },
    404: {
      cause:    'No se encontraron paises.',
      solution: 'Verificá el país seleccionado.',
    },
  },
  /* Operacion Tipo Iva Controller*/
  '/wsObtenerListadoOperacionTipoIva': {},
  /* forma de pago Controller */
  '/wsObtenerListadoFormaPago': {},
  /* Cobros Controller */
  '/wsObtenerListadoCobroPorEmpresa': {
    400: {
      cause: 'No se pudo obtener listado de cobro.',
    },
    401: {
      cause:    'No tenés autorización.',
      solution: 'Verificá que tengas permisos para operar.',
    },
    404: {
      cause: 'No se pudo obtener la información solicitada.',
    }
  },
  '/wsObtenerDatosCobroPorEmpresa': {
    401: {
      cause:    'No tenés autorización.',
      solution: 'Verificá que tengas permisos para operar.',
    },
    404: {
      cause: 'No se pudo obtener la información solicitada.',
    }
  },
  '/wsGenerarCobro': {
    401: {
      cause:    'No tenés autorización.',
      solution: 'Verificá que tengas permisos para operar.',
    }
  },
  '/wsEliminarCobro': {
    400: {
      cause: 'No se pudo eliminar el cobro.',
    },
    401: {
      cause:    'No tenés autorización.',
      solution: 'Verificá que tengas permisos para operar.',
    }
  },
  /* Movimiento Caja Controller */
  '/wsObtenerListadoCajaMovimientoPorEmpresa': {
    400: {
      cause: 'No se pudo obtener listado de movimiento de caja.',
    },
    401: {
      cause:    'No tenés autorización.',
      solution: 'Verificá que tengas permisos para operar.',
    },
    404: {
      cause: 'No se pudo obtener la información solicitada.',
    }
  },
  '/wsObtenerDatosCajaMovimientoPorEmpresa': {
    401: {
      cause:    'No tenés autorización.',
      solution: 'Verificá que tengas permisos para operar.',
    },
    404: {
      cause: 'No se pudo obtener la información solicitada.',
    }
  },
  '/wsGenerarCajaMovimiento': {
    400: {
      cause: 'No se pudo obtener listado de movimiento de caja.',
    },
    401: {
      cause:    'No tenés autorización.',
      solution: 'Verificá que tengas permisos para operar.',
    },
    404: {
      cause: 'No se pudo obtener la información solicitada.',
    }
  },
  '/wsEliminarCajaMovimiento': {
    400: {
      cause: 'Ocurrió un error al eliminar el movimiento de caja.',
    },
    401: {
      cause:    'No tenés autorización para eliminar el movimiento de caja.',
      solution: 'Verificá que tengas permisos para operar.',
    },
  },
  /* Cierre Caja Controller */
  '/wsObtenerListadoCajaCierrePorEmpresa': {
    400: {
      cause: 'Ocurrió un error al listar los cierres de caja.',
    },
    401: {
      cause:    'No tenés autorización para listar el cierre de caja.',
      solution: 'Verificá que tengas permisos para operar.',
    },
  },
  '/wsObtenerDatosCajaCierrePorEmpresa': {
    400: {
      cause: 'Ocurrió un error al obtener los datos del cierre de caja.',
    },
    401: {
      cause:    'No tenés autorización para obtener los datos del cierre de caja.',
      solution: 'Verificá que tengas permisos para operar.',
    }
  },
  '/wsGenerarCajaCierre': {
    400: {
      cause: 'Ocurrió un error al generar cierre de caja.',
    },
    401: {
      cause:    'No tenés autorización para generar cierre de caja.',
      solution: 'Verificá que tengas permisos para operar.',
    }
  },
  '/wsEliminarCajaCierre': {
    400: {
      cause: 'Ocurrió un error al eliminar cierre de caja.',
    },
    401: {
      cause:    'No tenés autorización para eliminar cierre de caja.',
      solution: 'Verificá que tengas permisos para operar.',
    }
  },
  /* Comprobante Controller */
  '/wsObtenerListadoComprobantes': {
    400: {
      cause: 'No se pudo obtener listado de comprobantes.',
    },
    401: {
      cause:    'No tenés autorización.',
      solution: 'Verificá que tengas permisos para operar.',
    },
    404: {
      cause: 'No se pudo obtener la información solicitada.',
    }
  },
  '/wsObtenerDatosComprobantePorEmpresa': {
    400: {
      cause: 'No se pudo obtener los datos del comprobante.',
    },
    401: {
      cause:    'No tenés autorización.',
      solution: 'Verificá que tengas permisos para operar.',
    },
    404: {
      cause: 'No se pudo obtener la información solicitada.',
    }
  },
  '/wsFacturarCobro': {
    400: {
      cause: 'No se pudo generar comprobante.',
    },
    401: {
      cause:    'No tenés autorización.',
      solution: 'Verificá que tengas permisos para operar.',
    },
    404: {
      cause: 'No se pudo obtener la información solicitada.',
    }
  },
  '/wsEnviarEmailComprobante': {
    400: {
      cause: 'No se pudo enviar el email del comprobantes',
    },
    401: {
      cause:    'No tenés autorización.',
      solution: 'Verificá que tengas permisos para operar.',
    },
    404: {
      cause: 'No se pudo obtener la información solicitada.',
    }
  },
  '/wsComprobanteParaDescargaComprador': {
    400: {
      cause: 'No se pudo obtener los datos del comprobante.',
    },
    401: {
      cause:    'No tenés autorización.',
      solution: 'Verificá que tengas permisos para operar.',
    },
    404: {
      cause: 'No se pudo obtener la información solicitada.',
    }
  },
  '/wsAnularFacturaCobro': {
    400: {
      cause: 'Ocurrió un error al generar la Nota de Crédito.',
    },
    401: {
      cause:    'No tenés autorización para generar la Nota de Crédito.',
      solution: 'Verificá que tengas permisos para operar.',
    },
  },
  '/wsDescargarAfipRegInfo': {
    400: {
      cause: 'Ocurrió un error al generar la descarga.',
    },
    401: {
      cause:    'No tenés autorización para generar la descarga.',
      solution: 'Verificá que tengas permisos para operar.',
    },
  }
};

// ─── Handler principal ────────────────────────────────────────────────────────

/**
 * Maneja errores HTTP mostrando alertas amigables al usuario.
 * Notifica al equipo de desarrollo cuando el error no está contemplado.
 * @returns `true` si el error fue silenciado, `false` en caso contrario.
 */
export function handleHttpError(
  error: HttpErrorResponse,
  alertService: AlertService,
  errorReporting: ErrorReportingService
): boolean {
  const matched = resolveErrorMessages(error);

  if (matched?.silent) return true;

  // Si no hay ningún mapeo conocido, notificar al programador.
  if (matched === undefined) {
    errorReporting.reportUnhandledError(error);
  }

  const { cause, solution } = matched ?? GENERIC_ERROR;

  // `solution` puede venir undefined: en ese caso el AlertService solo muestra
  // cause + support, sin párrafo intermedio redundante.
  alertService.showStructuredError({
    title: 'Ocurrió un error',
    cause,
    solution,
    confirmButtonText: 'ENTENDIDO',
  });

  return false;
}

// ─── Resolución de mensajes ───────────────────────────────────────────────────

function resolveErrorMessages(error: HttpErrorResponse): ErrorMessages | undefined {
  const endpointMatch = matchEndpoint(error.url);

  if (endpointMatch) {
    const msg = endpointMatch[error.status];
    if (msg !== undefined) return msg;
  }

  return HTTP_STATUS_MESSAGES[error.status];
}

/**
 * Resuelve el mapa de mensajes de un endpoint a partir de la URL del error.
 *
 * CP-DASH-027: antes el match era por substring sobre la URL completa
 * (endsWith / includes(ep+'?') / includes(ep+'/')). Eso dependía del orden de
 * declaración y era frágil si un endpoint era prefijo/sufijo de otro o si venían
 * query params. Ahora se extrae el pathname y se prioriza el match EXACTO;
 * el endsWith queda solo como fallback y, entre varios candidatos, gana el más
 * largo (el más específico), sin depender del orden del objeto.
 */
function matchEndpoint(
  url: string | null
): Partial<Record<number, ErrorMessages>> | null {
  if (!url) return null;

  // Quedarse solo con el path: sin protocolo, host ni query string.
  let path: string;
  try {
    path = new URL(url).pathname;          // 'https://api.../rest/login?x=1' -> '/rest/login'
  } catch {
    path = url.split('?')[0];              // fallback si url no es absoluta
  }

  const keys = Object.keys(ENDPOINT_MESSAGES);

  // 1) Match exacto: prioridad absoluta.
  if (path in ENDPOINT_MESSAGES) {
    return ENDPOINT_MESSAGES[path];
  }

  // 2) Fallback por sufijo, eligiendo la clave MÁS LARGA que matchee
  //    (la más específica), independiente del orden de declaración.
  let mejor: string | null = null;
  for (const ep of keys) {
    if (path.endsWith(ep) && (mejor === null || ep.length > mejor.length)) {
      mejor = ep;
    }
  }

  return mejor ? ENDPOINT_MESSAGES[mejor] : null;
}

// ════════════════════════════════════════════════════════════════════════════
// 2) GUARDA DE COLISIÓN (agregar al final del archivo, fuera de toda función)
// ════════════════════════════════════════════════════════════════════════════

/**
 * Chequeo defensivo para futuras implementaciones (CP-DASH-027).
 * Corre una sola vez al cargar el módulo. Si al agregar un endpoint nuevo su
 * clave resulta ser SUFIJO de otra ya existente, el fallback por endsWith podría
 * elegir la clave equivocada. Este aviso lo detecta en desarrollo antes de que
 * llegue a producción. No afecta el runtime del usuario: solo emite un warn.
 *
 * Si en el futuro se usa un environment, envolver el bloque en
 * `if (!environment.production) { ... }` para no ejecutarlo en prod.
 */
(function verificarColisionesEndpoints(): void {
  const keys = Object.keys(ENDPOINT_MESSAGES);
  const colisiones: Array<[string, string]> = [];

  for (const a of keys) {
    for (const b of keys) {
      // 'a' es sufijo de 'b' (b termina en a) siendo claves distintas.
      if (a !== b && b.endsWith(a)) {
        colisiones.push([a, b]);
      }
    }
  }

  if (colisiones.length > 0) {
    console.warn(
      '[handle-http-error] Colisión potencial de claves de endpoint. ' +
      'Estas claves son sufijo de otra y el match por endsWith podría elegir mal. ' +
      'Revisá que el pathname exacto resuelva al mapa correcto:',
      colisiones.map(([a, b]) => `'${a}' es sufijo de '${b}'`)
    );
  }
})();